import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactCalendar from "react-calendar";
import moment from "moment";
import { Icon } from "assets/icons";

import "react-calendar/dist/Calendar.css";
import "./calendar.css";
import styles from "./styles.module.scss";

const MENU_GAP = 4;
const MENU_EST_HEIGHT = 280;

/**
 * Поле даты с выпадающим календарём.
 * Календарь рендерится в portal (поверх футера модалок).
 */
export const Calendar = ({
  value,
  onChange,
  label,
  labelWidth,
  labelVariant = "medium",
  inputWidth = 130,
  placeholder = "ДД.ММ.ГГГГ",
  maxDate,
  className,
}) => {
  const containerRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const menuRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [localValue, setLocalValue] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);

  useEffect(() => {
    setLocalValue(value ? moment(value).format("DD.MM.YYYY") : "");
  }, [value]);

  useLayoutEffect(() => {
    if (!visible || !inputWrapperRef.current) {
      setMenuStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = inputWrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
      const openUp =
        spaceBelow < MENU_EST_HEIGHT && rect.top > spaceBelow + MENU_GAP;

      setMenuStyle({
        position: "fixed",
        left: rect.left,
        top: openUp ? undefined : rect.bottom + MENU_GAP,
        bottom: openUp ? window.innerHeight - rect.top + MENU_GAP : undefined,
        zIndex: 2000,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    // Скролл внутри модалки тоже двигает поле
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const handleClickOutside = (event) => {
      const inField = containerRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inField && !inMenu) setVisible(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visible]);

  const handleInput = (raw) => {
    let val = raw.replace(/\D/g, "");
    if (val.length > 2) val = `${val.slice(0, 2)}.${val.slice(2)}`;
    if (val.length > 5) val = `${val.slice(0, 5)}.${val.slice(5, 9)}`;
    setLocalValue(val);
  };

  const handleBlur = () => {
    const parsed = moment(localValue, "DD.MM.YYYY", true);
    if (parsed.isValid()) {
      onChange?.(parsed.toDate());
    } else {
      setLocalValue(value ? moment(value).format("DD.MM.YYYY") : "");
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${className || ""}`}
    >
      {!!label && (
        <span
          className={`${styles.label} ${
            labelVariant === "regular" ? styles.labelRegular : ""
          }`}
          style={labelWidth ? { width: labelWidth, flexShrink: 0 } : undefined}
        >
          {String(label).replace(/:$/, "")}:
        </span>
      )}
      <div
        ref={inputWrapperRef}
        className={styles.inputWrapper}
        onClick={() => setVisible(true)}
      >
        <input
          className={styles.input}
          type="text"
          value={localValue}
          placeholder={placeholder}
          style={{ width: inputWidth }}
          onChange={(ev) => handleInput(ev.target.value)}
          onBlur={handleBlur}
        />
        <Icon type="arrow" className={styles.icon} />
      </div>
      {visible &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.calendarWrapper}
            style={menuStyle}
          >
            <ReactCalendar
              maxDate={maxDate}
              value={value}
              onChange={(val) => {
                onChange?.(val);
                setVisible(false);
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
};
